import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const COLOR_PILL = {
  red: "text-rose-700 bg-rose-100 border-rose-200",
  amber: "text-amber-700 bg-amber-50 border-amber-200",
  slate: "text-slate-500 bg-slate-100 border-slate-200",
  green: "text-emerald-700 bg-emerald-50 border-emerald-200",
};

// The seven operational lenses, in the order Operations/Management triage them.
const TILES = [
  { key: "blocked", label: "Blocked", icon: "OctagonAlert", tone: "text-rose-600 bg-rose-50 ring-rose-100" },
  { key: "pending_management", label: "Needs Management Review", icon: "Gavel", tone: "text-amber-600 bg-amber-50 ring-amber-100" },
  { key: "waiting_on_client", label: "Waiting on Client", icon: "UserRoundClock", tone: "text-amber-600 bg-amber-50 ring-amber-100" },
  { key: "ready_for_assignment", label: "Ready for Assignment", icon: "UserPlus", tone: "text-blue-600 bg-blue-50 ring-blue-100" },
  { key: "ready_for_production", label: "Ready for Production", icon: "Factory", tone: "text-indigo-600 bg-indigo-50 ring-indigo-100" },
  { key: "ready_for_review", label: "Ready for Review", icon: "ClipboardCheck", tone: "text-emerald-600 bg-emerald-50 ring-emerald-100" },
  { key: "ready_for_filing", label: "Ready for Filing", icon: "BadgeCheck", tone: "text-emerald-600 bg-emerald-50 ring-emerald-100" },
];

const fmtDT = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function Tile({ tile, value, active, onClick }) {
  const I = Icons[tile.icon] || Icons.Circle;
  return (
    <button
      onClick={onClick}
      data-testid={`readiness-tile-${tile.key}`}
      className={cn(
        "text-left rounded-xl border p-3 transition-colors bg-white hover:border-slate-300",
        active ? "border-[#171B2E] ring-2 ring-[#171B2E]/10" : "border-slate-200"
      )}
    >
      <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center ring-4", tile.tone)}>
        <I className="h-4 w-4" />
      </div>
      <div className="text-2xl font-heading font-bold text-[#171B2E] mt-2">{value ?? 0}</div>
      <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{tile.label}</div>
    </button>
  );
}

export default function FirmReadinessBoard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { state: filter } : {};
      const [b, t] = await Promise.all([
        api.get("/dependency-runtime/firm/readiness-board", { params }),
        api.get("/dependency-runtime/telemetry", { params: { limit: 1 } }),
      ]);
      setData(b.data);
      setTelemetry(t.data.summary);
    } catch {
      toast.error("Could not load the Dependency Readiness Board");
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const runFirmRefresh = async () => {
    setRefreshing(true);
    const tid = toast.loading("Running Allison across every active engagement…");
    try {
      const r = await api.post("/dependency-runtime/firm/refresh");
      toast.success(`Readiness intelligence refreshed — ${r.data.engagements_evaluated} engagement(s), ${r.data.pending_management} pending Management`, { id: tid });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed", { id: tid });
    } finally {
      setRefreshing(false);
    }
  };

  const summary = data?.summary || {};
  const rows = data?.rows || [];
  const lfe = data?.last_firm_evaluation;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4" data-testid="firm-readiness-board">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-bold text-[#171B2E] flex items-center gap-2">
            <Icons.LayoutDashboard className="h-5 w-5 text-[#171B2E]" />
            Dependency Readiness Board
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Firm-wide engagement readiness — the operational heartbeat: what needs attention right now.
            {data?.gating_enabled === false ? (
              <span className="ml-1 inline-flex items-center gap-1 text-[10px] text-amber-600" data-testid="gating-state"><Icons.ShieldOff className="h-3 w-3" />Gating disabled — safety valve</span>
            ) : (
              <span className="ml-1 inline-flex items-center gap-1 text-[10px] text-emerald-500" data-testid="gating-state"><Icons.ShieldCheck className="h-3 w-3" />Gating active</span>
            )}
          </p>
          <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1.5" data-testid="last-firm-evaluation">
            <Icons.Clock className="h-3 w-3" />
            <span className="font-medium text-slate-500">Last Firm Evaluation:</span>
            {lfe?.at ? (
              <span>{new Date(lfe.at).toLocaleString(undefined, { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                {typeof lfe.engagements_evaluated === "number" ? ` · ${lfe.engagements_evaluated} engagements` : ""}</span>
            ) : <span className="italic">never — run a refresh to populate</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data?.can_refresh && (
            <button onClick={runFirmRefresh} disabled={refreshing} data-testid="refresh-readiness-intelligence"
                    className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-[#171B2E] hover:bg-[#171B2E]/90 text-white text-sm font-semibold disabled:opacity-60">
              {refreshing ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Sparkles className="h-4 w-4" />}
              Refresh Readiness Intelligence
            </button>
          )}
          <button onClick={load} data-testid="readiness-refresh" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
            <Icons.RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />Reload
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
        {TILES.map((t) => (
          <Tile key={t.key} tile={t} value={summary[t.key]} active={filter === t.key}
                onClick={() => setFilter(filter === t.key ? null : t.key)} />
        ))}
      </div>

      {telemetry && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-400 border-y border-slate-100 py-2" data-testid="readiness-telemetry">
          <span className="inline-flex items-center gap-1 text-slate-500 font-medium"><Icons.Activity className="h-3.5 w-3.5" />Allison evaluation telemetry</span>
          <span>{telemetry.runs} runs</span>
          <span>{telemetry.deterministic} deterministic</span>
          <span>{telemetry.allison} interpretive</span>
          <span className="text-amber-600">{telemetry.pending_management} pending mgmt</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-10 text-slate-400" data-testid="readiness-empty">
          <Icons.ShieldCheck className="h-9 w-9 mx-auto mb-2 text-emerald-400" />
          <p className="text-sm">{filter ? "No engagements in this state." : "No active engagements with dependencies yet."}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-3 font-medium">Engagement</th>
                <th className="py-2 pr-3 font-medium">Client</th>
                <th className="py-2 pr-3 font-medium">Services</th>
                <th className="py-2 pr-3 font-medium">Readiness</th>
                <th className="py-2 pr-3 font-medium">Blocking</th>
                <th className="py-2 pr-3 font-medium">Flags</th>
                <th className="py-2 pr-3 font-medium">Evaluated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.engagement_id} data-testid={`readiness-row-${r.engagement_id}`}
                    onClick={() => r.client_id && nav(`/client-workspace/${r.client_id}`)}
                    className="border-b border-slate-50 hover:bg-slate-50/70 cursor-pointer">
                  <td className="py-2.5 pr-3 font-medium text-[#171B2E] whitespace-nowrap">{r.engagement_number || "—"}</td>
                  <td className="py-2.5 pr-3 text-slate-600 whitespace-nowrap">{r.client_name || "—"}</td>
                  <td className="py-2.5 pr-3 text-slate-500 max-w-[220px] truncate" title={(r.services || []).join(", ")}>
                    {(r.services || []).join(", ") || "—"}
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className={cn("inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border font-medium", COLOR_PILL[r.readiness?.color] || COLOR_PILL.slate)}>
                      {r.readiness?.label}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 text-slate-600">
                    {r.blocking_count > 0 ? (
                      <span title={(r.blocking || []).join(", ")} className="text-rose-600 font-medium">{r.blocking_count}</span>
                    ) : <span className="text-slate-300">0</span>}
                  </td>
                  <td className="py-2.5 pr-3">
                    <div className="flex items-center gap-1.5">
                      {r.pending_management_count > 0 && <span title={(r.pending_management_items || []).join(", ")} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 cursor-help">Mgmt {r.pending_management_count}</span>}
                      {r.overrides_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">Ovr {r.overrides_count}</span>}
                      {r.waiting_on_client_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">Client {r.waiting_on_client_count}</span>}
                      {!r.pending_management_count && !r.overrides_count && !r.waiting_on_client_count && <span className="text-slate-300 text-xs">—</span>}
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 text-[11px] text-slate-400 whitespace-nowrap">{fmtDT(r.last_evaluated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
